import React, { useState, useEffect, useRef } from 'react';
import { Users, ExternalLink, Building2, Globe, Lightbulb, Mic, UserCheck, Award, Users2, TrendingUp, Handshake, DollarSign, MapPin, Calendar, Target, GraduationCap, Briefcase, Star, X } from 'lucide-react';

const TeamPage = ({ content = {}, language = 'en' }) => {
  const [teamVisible, setTeamVisible] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const teamRef = useRef(null);

  // Default content structure
  const defaultContent = {
    en: {
      team: {
        title: "Our Team"
      }
    },
    es: {
      team: {
        title: "Nuestro Equipo"
      }
    }
  };

  const pageContent = content.en ? content : defaultContent;

  useEffect(() => {
    const isInViewport = (element, offset = 100) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.top <= (window.innerHeight - offset) && rect.bottom >= offset;
    };
    
    const handleScroll = () => {
      if (teamRef.current && isInViewport(teamRef.current)) {
        setTeamVisible(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle modal open
  const handleCardClick = (person, isFounder = false) => {
    setSelectedPerson({ ...person, isFounder });
  };

  // Handle modal close
  const closeModal = () => {
    setSelectedPerson(null);
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    
    if (selectedPerson) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [selectedPerson]);

  // Zeus's comprehensive details
  const zeusDetails = {
    en: {
      quickFacts: [
        { icon: GraduationCap, label: "Education", value: "UCLA Student (3rd Year)" },
        { icon: Building2, label: "Focus", value: "Real Estate Development" },
        { icon: Globe, label: "Markets", value: "Americas (US & LATAM)" },
        { icon: Target, label: "Specialty", value: "AI Integration in AEC" },
        { icon: Mic, label: "Media", value: "The Infinity One Podcast" },
        { icon: Calendar, label: "Founded", value: "Infinity Development" }
      ],
      experience: "With hands-on experience in construction, entitlements, capital planning, and strategic partnerships, Zeus is currently sourcing his first infill development project in Los Angeles while leading public-private initiatives to rebuild fire-impacted communities.",
      vision: "Passionate about integrating AI into the AEC (Architecture, Engineering, Construction) sector, Zeus is committed to empowering a new generation of developers to build more efficiently, sustainably, and globally. His long-term vision positions Infinity Development as a cross-continental platform driving innovation, capital, and purpose into underserved and emerging markets.",
      background: "A former athlete, Zeus approaches each venture with a competitive edge, systems thinking, and a deep sense of purpose. He's building not just real estate, but a long-term movement for impact, innovation, and legacy across the Americas.",
      podcast: "Host of The Infinity One Podcast, a bilingual media platform featuring top voices in real estate, venture capital, and global innovation — offering insights that help investors and founders build generational companies and reimagine the built world.",
      achievements: [
        "Founder & CEO of Infinity Development",
        "Leading fire-impacted community rebuilding initiatives",
        "Building cross-border investor network across LATAM",
        "Integrating AI technology into traditional real estate development",
        "Host of bilingual industry-leading podcast"
      ]
    },
    es: {
      quickFacts: [
        { icon: GraduationCap, label: "Educación", value: "Estudiante UCLA (3er Año)" },
        { icon: Building2, label: "Enfoque", value: "Desarrollo Inmobiliario" },
        { icon: Globe, label: "Mercados", value: "Américas (US y LATAM)" },
        { icon: Target, label: "Especialidad", value: "Integración IA en AEC" },
        { icon: Mic, label: "Medios", value: "The Infinity One Podcast" },
        { icon: Calendar, label: "Fundó", value: "Infinity Development" }
      ],
      experience: "Con experiencia práctica en construcción, permisos, planificación de capital y alianzas estratégicas, Zeus está actualmente buscando su primer proyecto de desarrollo de relleno en Los Ángeles mientras lidera iniciativas público-privadas para reconstruir comunidades afectadas por incendios.",
      vision: "Apasionado por integrar IA en el sector AEC (Arquitectura, Ingeniería, Construcción), Zeus está comprometido con empoderar a una nueva generación de desarrolladores para construir de manera más eficiente, sostenible y global. Su visión a largo plazo posiciona a Infinity Development como una plataforma transcontinental.",
      background: "Ex atleta, Zeus aborda cada empresa con una ventaja competitiva, pensamiento sistémico y un profundo sentido de propósito. Está construyendo no solo bienes raíces, sino un movimiento a largo plazo por el impacto, la innovación y el legado en las Américas.",
      podcast: "Anfitrión de The Infinity One Podcast, una plataforma de medios bilingüe que presenta las principales voces en bienes raíces, capital de riesgo e innovación global — ofreciendo insights que ayudan a inversionistas y fundadores a construir empresas generacionales.",
      achievements: [
        "Fundador y CEO de Infinity Development",
        "Liderando iniciativas de reconstrucción de comunidades afectadas",
        "Construyendo red de inversionistas transfronteriza en LATAM",
        "Integrando tecnología IA en desarrollo inmobiliario tradicional",
        "Anfitrión de podcast bilingüe líder en la industria"
      ]
    }
  };

  // Strategic advisors with comprehensive details
  const advisors = {
    en: [
      {
        id: 'advisor-1',
        name: "Strategic Advisors",
        title: "Industry Veterans & Market Leaders",
        company: "Leading Development Firms",
        image: null,
        quickFacts: [
          { icon: Award, label: "Experience", value: "25+ Years Combined" },
          { icon: Building2, label: "Projects", value: "$1B+ Developed" },
          { icon: Globe, label: "Markets", value: "Americas & Europe" },
          { icon: Briefcase, label: "Expertise", value: "Cross-Border Development" },
          { icon: TrendingUp, label: "Track Record", value: "50+ Successful Projects" }
        ],
        fullBio: "Our strategic advisors bring decades of experience in real estate development, venture capital, and cross-border business operations. They provide crucial insights into market dynamics, regulatory environments, and strategic partnerships across the Americas. Their expertise spans from large-scale urban development to emerging market entry strategies.",
        expertise: [
          "Large-scale urban development projects",
          "Cross-border investment structuring",
          "Regulatory navigation across multiple jurisdictions",
          "Public-private partnership development",
          "Sustainable development practices"
        ],
        linkedin: "#"
      },
      {
        id: 'advisor-2',
        name: "Technology Partners",
        title: "AI & PropTech Innovation Leaders",
        company: "Leading Tech Firms",
        image: null,
        quickFacts: [
          { icon: Lightbulb, label: "Focus", value: "AI Integration & PropTech" },
          { icon: Target, label: "Sector", value: "AEC Technology" },
          { icon: TrendingUp, label: "Innovation", value: "Next-Gen Solutions" },
          { icon: Star, label: "Portfolio", value: "20+ Tech Companies" },
          { icon: Globe, label: "Reach", value: "Global Technology Network" }
        ],
        fullBio: "Leading innovators in AI integration and property technology solutions. They help us leverage cutting-edge technologies to optimize development processes, enhance sustainability metrics, and create more efficient building solutions. Their network spans the most advanced PropTech companies globally.",
        expertise: [
          "AI-powered construction optimization",
          "Smart building technology integration",
          "PropTech ecosystem development",
          "Sustainable technology implementation",
          "Data analytics for real estate development"
        ],
        linkedin: "#"
      }
    ],
    es: [
      {
        id: 'advisor-1',
        name: "Asesores Estratégicos",
        title: "Veteranos de la Industria y Líderes del Mercado",
        company: "Firmas de Desarrollo Líderes",
        image: null,
        quickFacts: [
          { icon: Award, label: "Experiencia", value: "25+ Años Combinados" },
          { icon: Building2, label: "Proyectos", value: "$1B+ Desarrollados" },
          { icon: Globe, label: "Mercados", value: "Américas y Europa" },
          { icon: Briefcase, label: "Experiencia", value: "Desarrollo Transfronterizo" },
          { icon: TrendingUp, label: "Historial", value: "50+ Proyectos Exitosos" }
        ],
        fullBio: "Nuestros asesores estratégicos aportan décadas de experiencia en desarrollo inmobiliario, capital de riesgo y operaciones comerciales transfronterizas. Proporcionan insights cruciales sobre dinámicas de mercado, entornos regulatorios y alianzas estratégicas en las Américas.",
        expertise: [
          "Proyectos de desarrollo urbano a gran escala",
          "Estructuración de inversiones transfronterizas",
          "Navegación regulatoria en múltiples jurisdicciones",
          "Desarrollo de asociaciones público-privadas",
          "Prácticas de desarrollo sostenible"
        ],
        linkedin: "#"
      },
      {
        id: 'advisor-2',
        name: "Socios Tecnológicos",
        title: "Líderes en Innovación IA y PropTech",
        company: "Firmas Tecnológicas Líderes",
        image: null,
        quickFacts: [
          { icon: Lightbulb, label: "Enfoque", value: "Integración IA y PropTech" },
          { icon: Target, label: "Sector", value: "Tecnología AEC" },
          { icon: TrendingUp, label: "Innovación", value: "Soluciones Avanzadas" },
          { icon: Star, label: "Portafolio", value: "20+ Empresas Tech" },
          { icon: Globe, label: "Alcance", value: "Red Tecnológica Global" }
        ],
        fullBio: "Innovadores líderes en integración de IA y soluciones de tecnología inmobiliaria. Nos ayudan a aprovechar tecnologías de vanguardia para optimizar procesos de desarrollo, mejorar métricas de sostenibilidad y crear soluciones de construcción más eficientes.",
        expertise: [
          "Optimización de construcción con IA",
          "Integración de tecnología de edificios inteligentes",
          "Desarrollo de ecosistemas PropTech",
          "Implementación de tecnología sostenible",
          "Análisis de datos para desarrollo inmobiliario"
        ],
        linkedin: "#"
      }
    ]
  };

  // Investor cards with comprehensive details
  const investors = {
    en: [
      {
        id: 'investor-1',
        name: "Institutional Partners",
        title: "Strategic Capital Partners",
        company: "Coming Soon",
        image: null,
        quickFacts: [
          { icon: TrendingUp, label: "Focus", value: "Sustainable Development" },
          { icon: DollarSign, label: "Capital", value: "Growth Stage Funding" },
          { icon: Globe, label: "Reach", value: "Cross-Border Expertise" },
          { icon: Building2, label: "Sector", value: "Real Estate & PropTech" },
          { icon: Award, label: "Track Record", value: "Proven Portfolio" }
        ],
        fullBio: "We're in active discussions with leading institutional investors who share our vision for sustainable, cross-border real estate development. These partnerships will provide the capital foundation for our expansion across the Americas while maintaining our commitment to community impact and environmental responsibility.",
        partnership: [
          "Series A funding for market expansion",
          "Strategic guidance on institutional-grade projects",
          "Access to global investment networks",
          "ESG compliance and sustainability metrics",
          "Long-term capital partnership structure"
        ],
        status: "coming-soon"
      },
      {
        id: 'investor-2',
        name: "Private Equity",
        title: "Growth & Expansion Partners",
        company: "Coming Soon",
        image: null,
        quickFacts: [
          { icon: TrendingUp, label: "Stage", value: "Series A & Beyond" },
          { icon: Target, label: "Focus", value: "Market Expansion" },
          { icon: Building2, label: "Sector", value: "Real Estate Technology" },
          { icon: Globe, label: "Geography", value: "Americas Focus" },
          { icon: Star, label: "Strategy", value: "Platform Building" }
        ],
        fullBio: "Our private equity partnerships are being structured to support rapid market expansion while maintaining our commitment to sustainable development practices and community impact across emerging markets. These relationships will accelerate our platform development and cross-border capabilities.",
        partnership: [
          "Platform scaling and market expansion",
          "Operational excellence and best practices",
          "Strategic acquisitions and partnerships",
          "Technology integration and innovation",
          "Exit strategy planning and execution"
        ],
        status: "coming-soon"
      },
      {
        id: 'investor-3',
        name: "Angel Network",
        title: "Strategic Angels & Family Offices",
        company: "Coming Soon",
        image: null,
        quickFacts: [
          { icon: Handshake, label: "Type", value: "HNW Individuals & Family Offices" },
          { icon: Globe, label: "Network", value: "Americas-Focused Investors" },
          { icon: Lightbulb, label: "Expertise", value: "PropTech & AI Innovation" },
          { icon: Building2, label: "Experience", value: "Real Estate Veterans" },
          { icon: TrendingUp, label: "Value-Add", value: "Strategic Mentorship" }
        ],
        fullBio: "Building a network of sophisticated angel investors and family offices who understand the unique opportunities in cross-border real estate development and the integration of AI technologies in the built environment. These partners bring both capital and strategic expertise to accelerate our growth.",
        partnership: [
          "Early-stage funding and strategic guidance",
          "Industry connections and partnership opportunities",
          "Mentorship from successful real estate entrepreneurs",
          "Access to proprietary deal flow and markets",
          "Long-term strategic advisory relationships"
        ],
        status: "coming-soon"
      }
    ],
    es: [
      {
        id: 'investor-1',
        name: "Socios Institucionales",
        title: "Socios de Capital Estratégico",
        company: "Próximamente",
        image: null,
        quickFacts: [
          { icon: TrendingUp, label: "Enfoque", value: "Desarrollo Sostenible" },
          { icon: DollarSign, label: "Capital", value: "Financiamiento de Crecimiento" },
          { icon: Globe, label: "Alcance", value: "Experiencia Transfronteriza" },
          { icon: Building2, label: "Sector", value: "Bienes Raíces y PropTech" },
          { icon: Award, label: "Historial", value: "Portafolio Comprobado" }
        ],
        fullBio: "Estamos en discusiones activas con inversionistas institucionales líderes que comparten nuestra visión de desarrollo inmobiliario sostenible y transfronterizo. Estas asociaciones proporcionarán la base de capital para nuestra expansión mientras mantenemos nuestro compromiso con el impacto comunitario.",
        partnership: [
          "Financiamiento Serie A para expansión de mercado",
          "Orientación estratégica en proyectos de grado institucional",
          "Acceso a redes de inversión globales",
          "Cumplimiento ESG y métricas de sostenibilidad",
          "Estructura de asociación de capital a largo plazo"
        ],
        status: "coming-soon"
      },
      {
        id: 'investor-2',
        name: "Capital Privado",
        title: "Socios de Crecimiento y Expansión",
        company: "Próximamente",
        image: null,
        quickFacts: [
          { icon: TrendingUp, label: "Etapa", value: "Serie A y Más" },
          { icon: Target, label: "Enfoque", value: "Expansión de Mercado" },
          { icon: Building2, label: "Sector", value: "Tecnología Inmobiliaria" },
          { icon: Globe, label: "Geografía", value: "Enfoque Américas" },
          { icon: Star, label: "Estrategia", value: "Construcción de Plataforma" }
        ],
        fullBio: "Nuestras asociaciones de capital privado están siendo estructuradas para apoyar la rápida expansión del mercado mientras mantenemos nuestro compromiso con prácticas de desarrollo sostenible e impacto comunitario en mercados emergentes.",
        partnership: [
          "Escalamiento de plataforma y expansión de mercado",
          "Excelencia operacional y mejores prácticas",
          "Adquisiciones estratégicas y asociaciones",
          "Integración tecnológica e innovación",
          "Planificación y ejecución de estrategia de salida"
        ],
        status: "coming-soon"
      },
      {
        id: 'investor-3',
        name: "Red de Ángeles",
        title: "Ángeles Estratégicos y Oficinas Familiares",
        company: "Próximamente",
        image: null,
        quickFacts: [
          { icon: Handshake, label: "Tipo", value: "Individuos HNW y Oficinas Familiares" },
          { icon: Globe, label: "Red", value: "Inversionistas Enfocados en Américas" },
          { icon: Lightbulb, label: "Experiencia", value: "Innovación PropTech e IA" },
          { icon: Building2, label: "Experiencia", value: "Veteranos de Bienes Raíces" },
          { icon: TrendingUp, label: "Valor Agregado", value: "Mentoría Estratégica" }
        ],
        fullBio: "Construyendo una red de inversionistas ángeles sofisticados y oficinas familiares que entienden las oportunidades únicas en desarrollo inmobiliario transfronterizo y la integración de tecnologías de IA en el entorno construido.",
        partnership: [
          "Financiamiento temprano y orientación estratégica",
          "Conexiones de la industria y oportunidades de asociación",
          "Mentoría de emprendedores inmobiliarios exitosos",
          "Acceso a flujo de negocios y mercados propietarios",
          "Relaciones de asesoría estratégica a largo plazo"
        ],
        status: "coming-soon"
      }
    ]
  };

  // Professional Card Component
  const ProfessionalCard = ({ person, isFounder = false }) => {
    return (
      <div 
        className="group cursor-pointer"
        onClick={() => handleCardClick(person, isFounder)}
      >
        <div className="h-[450px] bg-gray-900 bg-opacity-40 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-gray-700 hover:shadow-3xl hover:bg-opacity-60 transition-all duration-300 group-hover:-translate-y-2 group-hover:scale-105">
          {/* Image Section */}
          <div className="h-80 bg-gradient-to-br from-gray-800 to-gray-900 relative overflow-hidden">
            {person.name === "Zeus Hernandez" ? (
              <img 
                src="zeus.png" 
                alt="Zeus Hernandez" 
                className="w-full h-full object-cover object-center"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div className={`w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center ${person.name === "Zeus Hernandez" ? 'hidden' : ''}`}>
              <Users className="w-32 h-32 text-gray-500" />
            </div>
            
            {/* Status badge for coming soon items */}
            {person.status === 'coming-soon' && (
              <div className="absolute top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                Coming Soon
              </div>
            )}
            
            {/* Click indicator */}
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm font-medium">
              Click to learn more
            </div>
          </div>
          
          {/* Content Section */}
          <div className="p-6 flex flex-col justify-center">
            <h3 className="text-2xl font-bold text-white mb-2">{person.name}</h3>
            <p className="text-gray-300 text-base font-medium mb-2">{person.title}</p>
            {person.company && (
              <p className="text-gray-400 text-sm">{person.company}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Modal Component
  const PersonModal = ({ person, onClose }) => {
    if (!person) return null;

    const details = person.isFounder ? zeusDetails[language] : null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl shadow-3xl border border-gray-700 w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-6 flex justify-between items-start">
            <div className="flex gap-6">
              {/* Image */}
              <div className="w-24 h-24 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl overflow-hidden flex-shrink-0">
                {person.name === "Zeus Hernandez" ? (
                  <img 
                    src="zeus.png" 
                    alt="Zeus Hernandez" 
                    className="w-full h-full object-cover object-center"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className={`w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center ${person.name === "Zeus Hernandez" ? 'hidden' : ''}`}>
                  <Users className="w-12 h-12 text-gray-500" />
                </div>
              </div>
              
              {/* Title Info */}
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">{person.name}</h2>
                <p className="text-xl text-gray-300 mb-1">{person.title}</p>
                {person.company && (
                  <p className="text-gray-400">{person.company}</p>
                )}
                {person.status === 'coming-soon' && (
                  <div className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium mt-2">
                    Coming Soon
                  </div>
                )}
              </div>
            </div>
            
            {/* Close Button */}
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid lg:grid-cols-3 gap-8">
              
              {/* Main Content - Left Side */}
              <div className="lg:col-span-2 space-y-6">
                
                {/* Overview */}
                <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Award className="w-5 h-5 mr-2 text-blue-400" />
                    Overview
                  </h3>
                  <p className="text-gray-300 leading-relaxed text-base">
                    {person.fullBio || (person.isFounder ? details?.experience : "Details coming soon...")}
                  </p>
                </div>

                {/* Additional sections for founders */}
                {person.isFounder && details && (
                  <>
                    <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                        <Target className="w-5 h-5 mr-2 text-blue-400" />
                        Vision & Mission
                      </h3>
                      <p className="text-gray-300 leading-relaxed text-base mb-4">
                        {details.vision}
                      </p>
                      <p className="text-gray-300 leading-relaxed text-base">
                        {details.background}
                      </p>
                    </div>
                    
                    <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                        <Star className="w-5 h-5 mr-2 text-blue-400" />
                        Key Achievements
                      </h3>
                      <ul className="space-y-3">
                        {details.achievements.map((achievement, index) => (
                          <li key={index} className="text-gray-300 text-base flex items-start">
                            <span className="text-blue-400 mr-3 mt-1">•</span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                      <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                        <Mic className="w-5 h-5 mr-2 text-blue-400" />
                        Media & Thought Leadership
                      </h3>
                      <p className="text-gray-300 leading-relaxed text-base">
                        {details.podcast}
                      </p>
                    </div>
                  </>
                )}

                {/* Expertise sections for advisors/investors */}
                {(person.expertise || person.partnership) && (
                  <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                    <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-blue-400" />
                      {person.expertise ? 'Areas of Expertise' : 'Partnership Focus'}
                    </h3>
                    <ul className="space-y-3">
                      {(person.expertise || person.partnership || []).map((item, index) => (
                        <li key={index} className="text-gray-300 text-base flex items-start">
                          <span className="text-blue-400 mr-3 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Sidebar - Right Side */}
              <div className="space-y-6">
                
                {/* Quick Facts */}
                <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-blue-400" />
                    Quick Facts
                  </h3>
                  <div className="space-y-4">
                    {(person.quickFacts || details?.quickFacts || []).map((fact, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <fact.icon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-400 text-sm">{fact.label}</p>
                          <p className="text-white text-base font-medium">{fact.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-gray-800 bg-opacity-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                    <ExternalLink className="w-5 h-5 mr-2 text-blue-400" />
                    Connect
                  </h3>
                  {person.linkedin && person.linkedin !== '#' ? (
                    <a 
                      href={person.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors font-medium w-full justify-center"
                    >
                      <ExternalLink className="w-5 h-5" />
                      LinkedIn Profile
                    </a>
                  ) : (
                    <div className="text-center p-4 bg-gray-700 bg-opacity-50 rounded-lg">
                      <p className="text-gray-400 text-sm">LinkedIn Coming Soon</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      <div 
        ref={teamRef}
        className="max-w-7xl mx-auto px-6 md:px-12"
      >
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className={`text-5xl font-bold mb-6 text-white transition-all duration-700 ${teamVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
            {pageContent[language].team.title}
          </h1>
          <div className={`h-1 w-32 bg-gradient-to-r from-blue-500 to-green-500 mx-auto transition-all duration-700 delay-200 ${teamVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}></div>
        </div>

        {/* Leadership Section */}
        <div className={`mb-20 transition-all duration-700 delay-300 ${teamVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl font-bold mb-12 text-center text-white">
            {language === 'en' ? 'Leadership' : 'Liderazgo'}
          </h2>
          
          <div className="grid md:grid-cols-1 gap-8 max-w-lg mx-auto">
            <ProfessionalCard 
              person={{
                id: 'zeus',
                name: "Zeus Hernandez",
                title: "Founder & CEO",
                company: "Infinity Development",
                linkedin: "https://www.linkedin.com/in/zeus-hernandez/"
              }} 
              isFounder={true}
            />
          </div>
        </div>

        {/* Strategic Advisors Section */}
        <div className={`mb-20 transition-all duration-700 delay-500 ${teamVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl font-bold mb-12 text-center text-white">
            {language === 'en' ? 'Strategic Advisors' : 'Asesores Estratégicos'}
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {advisors[language].map((advisor) => (
              <ProfessionalCard key={advisor.id} person={advisor} />
            ))}
          </div>
        </div>

        {/* Investment Partners Section */}
        <div className={`mb-20 transition-all duration-700 delay-600 ${teamVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <h2 className="text-3xl font-bold mb-12 text-center text-white">
            {language === 'en' ? 'Investment Partners' : 'Socios de Inversión'}
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {investors[language].map((investor) => (
              <ProfessionalCard key={investor.id} person={investor} />
            ))}
          </div>
        </div>

        {/* Join Our Mission CTA */}
        <div className={`bg-gradient-to-r from-gray-900 to-black rounded-2xl p-12 text-center transition-all duration-700 delay-700 ${teamVisible ? 'opacity-100 transform-none' : 'opacity-0 translate-y-10'}`}>
          <Users2 className="w-20 h-20 text-white mx-auto mb-8" />
          <h3 className="text-4xl font-bold mb-6 text-white">
            {language === 'en' ? 'Join Our Mission' : 'Únete a Nuestra Misión'}
          </h3>
          <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg leading-relaxed">
            {language === 'en'
              ? 'Are you passionate about sustainable development and cross-border innovation? We\'re building the future of real estate and looking for exceptional talent to join our movement.'
              : '¿Eres apasionado por el desarrollo sostenible y la innovación transfronteriza? Estamos construyendo el futuro de los bienes raíces y buscamos talento excepcional para unirse a nuestro movimiento.'}
          </p>
          <button className="bg-white text-black px-10 py-4 text-lg font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
            {language === 'en' ? 'View Opportunities' : 'Ver Oportunidades'}
          </button>
        </div>
      </div>

      {/* Modal */}
      {selectedPerson && (
        <PersonModal 
          person={selectedPerson} 
          onClose={closeModal}
        />
      )}

      <style jsx>{`
        .shadow-3xl {
          box-shadow: 0 35px 60px -12px rgba(0, 0, 0, 0.25);
        }
      `}</style>
    </div>
  );
};

export default TeamPage;